// Per-user sim funnel counts for the Simulations page Users card and the High
// Click Users drill, windowed to the selected period (BD-1003). The server
// user_roster aggregation windows these counts; the risk score stays the fixed
// 180-day snapshot.
import { useMemo } from 'react';
import useDashboardData from './hooks/useDashboardData';
import { useExcludeDisabled } from './disabledFilter';
import { toServerScope, userScopeIsEmails } from './serverScope';

const rate = (n, sent) => (sent ? Math.round((n / sent) * 100) : 0);

export const CLICK_USERS_LIMIT = 5000;

export function mapRosterClickRow(r) {
    const sent = r.simsSent || 0;
    const clicked = r.simsClicked || 0;
    const reported = r.simsReported || 0;
    return {
        name: `${r.firstName || ''} ${r.lastName || ''}`.trim() || r.email,
        email: r.email, dept: r.dept || '', location: r.location || '',
        risk: r.riskScore ?? null,
        sent, clicked, secondStage: r.simsLanded || 0, reported,
        clickRate: rate(clicked, sent), reportRate: rate(reported, sent),
    };
}

// Same row shape from a local (already windowed and scoped) send log.
export function buildClickUserStats(log, riskByEmail) {
    const byEmail = new Map();
    for (const e of log || []) {
        if (!e.email) continue;
        const u = byEmail.get(e.email) || {
            name: `${e.firstName || ''} ${e.lastName || ''}`.trim() || e.email,
            email: e.email, dept: e.dept || '', location: e.location || '',
            risk: riskByEmail?.get(e.email) ?? null,
            sent: 0, clicked: 0, secondStage: 0, reported: 0,
        };
        u.sent += 1;
        if (e.status === 'clicked' || e.status === 'clicked_2nd') u.clicked += 1;
        if (e.status === 'clicked_2nd') u.secondStage += 1;
        if (e.status === 'reported') u.reported += 1;
        byEmail.set(e.email, u);
    }
    return [...byEmail.values()].map((u) => ({ ...u, clickRate: rate(u.clicked, u.sent), reportRate: rate(u.reported, u.sent) }));
}

// Server rows (null while loading, unavailable, or when a user scope holds display
// names the server can't match). Card and drill pass the same args so they share
// one cached request.
function useRosterQuery({ scope, period, startDate, endDate }, sortField, skip) {
    const excludeDisabled = useExcludeDisabled();
    const emailsOnly = userScopeIsEmails(scope);
    const q = useDashboardData({
        metric: 'users', aggregation: 'user_roster', limit: CLICK_USERS_LIMIT,
        sort: { field: sortField, direction: sortField === 'completion' ? 'asc' : 'desc' },
        includeDisabled: !excludeDisabled,
        scope: toServerScope(scope),
        period, startDate, endDate,
    }, { skip: skip || !emailsOnly });
    return { q, emailsOnly };
}

function useRosterRows(args, sortField, skip) {
    const { q, emailsOnly } = useRosterQuery(args, sortField, skip);
    return emailsOnly && Array.isArray(q.data?.roster) ? q.data.roster : null;
}

// BD-1196: the roster request's loading/error state for WidgetState. Same args as
// useClickUsers / useCourseUsers, so it's the same cached request, not a second one.
export const useClickUsersQuery = (args, { skip = false } = {}) => useRosterQuery(args, 'clickRate', skip).q;
export const useCourseUsersQuery = (args, { skip = false } = {}) => useRosterQuery(args, 'completion', skip).q;

export function useClickUsers(args, { skip = false } = {}) {
    const roster = useRosterRows(args, 'clickRate', skip);
    return useMemo(() => (roster ? roster.map(mapRosterClickRow).filter((u) => u.sent > 0) : null), [roster]);
}

export function mapRosterCourseRow(r) {
    const sent = r.coursesSent || 0;
    const completed = r.coursesCompleted || 0;
    return {
        email: r.email,
        name: `${r.firstName || ''} ${r.lastName || ''}`.trim() || r.email,
        sent, completed,
        outstanding: sent - completed,
        completionRate: rate(completed, sent),
    };
}

export function useCourseUsers(args, { skip = false } = {}) {
    const roster = useRosterRows(args, 'completion', skip);
    return useMemo(() => {
        if (!roster || (roster.length && roster.every((r) => r.coursesCompleted === undefined))) return null;
        return roster.map(mapRosterCourseRow).filter((u) => u.sent > 0);
    }, [roster]);
}
